/**
 * Tracking Analytics Plugin (Client-only)
 *
 * Initializes GA4 + PostHog analytics providers and injects as $trackingAnalytics.
 * Gated behind !IS_ON_PREM via extendPlugins() in nuxt.config.js.
 */

import { Context, Inject } from '@nuxt/types/app'
import { CookieConsentService } from '~/services/tracking/CookieConsentService'
import { TrackingAnalyticsService } from '~/services/tracking/TrackingAnalyticsService'
import { createGoogleAnalyticsProvider } from '~/services/tracking/providers/googleAnalytics'
import { createPosthogProvider } from '~/services/tracking/providers/posthog'

declare module 'vue/types/vue' {
  interface Vue {
    $trackingAnalytics: TrackingAnalyticsService
  }
}

declare module 'vuex/types/index' {
  // skipcq: JS-0387, JS-0356
  interface Store<S> {
    $trackingAnalytics: TrackingAnalyticsService
  }
}

declare module '@nuxt/types' {
  interface Context {
    $trackingAnalytics: TrackingAnalyticsService
  }
}

export default (_context: Context, inject: Inject): void => {
  const { $config } = _context

  const consentService = CookieConsentService.getInstance()
  const trackingAnalytics = new TrackingAnalyticsService()

  // Register GA4 provider
  const measurementId = $config.googleAnalyticsMeasurementId
  if (measurementId) {
    const ga4Provider = createGoogleAnalyticsProvider({ measurementId }, consentService)
    trackingAnalytics.registerProvider(ga4Provider)
  }

  // Register PostHog provider
  const posthogPublicKey = $config.posthogPublicKey
  const posthogApiHost = $config.posthogApiHost
  const posthogUIHost = $config.posthogUIHost
  if (posthogPublicKey) {
    const posthogProvider = createPosthogProvider({
      publicKey: posthogPublicKey,
      apiHost: posthogApiHost || 'https://us.i.posthog.com',
      uiHost: posthogUIHost || 'https://us.posthog.com'
    })
    trackingAnalytics.registerProvider(posthogProvider)
  }

  inject('trackingAnalytics', trackingAnalytics)
}
